import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";

import { Card, ICardTitle, Loader, InfoBlockForm } from "shared/components";
import { Spacer } from "shared/layout";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IStatus, IInfoBlock, IInfoBlockAPIOrigin } from "shared/types";
import { EditInfoBlockItems } from "Venues/containers";
import { useVenuePageContext } from "Venues/contexts";

import { dataMetaSelector, clearActionMeta, editInfoBlock, createInfoBlock } from "Venues/state";
import {
  IInfoBlockType,
  VENUE_ACTIONS,
  IVenueBaseInfoBlock,
  IInfoBlockWithPath,
} from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

interface EditInfoBlockProps {
  icon?: React.ComponentType<any>;
  title: ICardTitle;
  infoBlock?: IVenueBaseInfoBlock;
  infoBlockType: {
    parentLabel: string;
    label: string;
    type: IInfoBlockType;
  };
  submitData: { type: "stage" | "venue"; id: string };
}

export const EditInfoBlock: React.FC<EditInfoBlockProps> = ({
  icon,
  title,
  infoBlock,
  infoBlockType,
  submitData,
}) => {
  const dispatch = useDispatch();
  const { fetchingVenueInfoBlocks, userPermissions } = useVenuePageContext();

  const [apiOrigin, setApiOrigin] = useState<IInfoBlockAPIOrigin>(IInfoBlockAPIOrigin.Form);

  const [action, setAction] = useState<string>();

  useClearActionMetaOnUnMount(action || "");

  const { api: apiMeta } = useSelector(dataMetaSelector(action || ""));

  useEffect(() => {
    if (!fetchingVenueInfoBlocks && action === undefined) {
      setAction(infoBlock ? VENUE_ACTIONS.editInfoBlock : VENUE_ACTIONS.createInfoBlock);
    }
  }, [fetchingVenueInfoBlocks, infoBlock, action]);

  // successful api call
  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded && action) {
      // if API is called from the form, show toast message
      if (apiOrigin === IInfoBlockAPIOrigin.Form) {
        dispatch(clearActionMeta(action));
        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: `${
              action === VENUE_ACTIONS.editInfoBlock ? copy.actions.edited : copy.actions.added
            } ${infoBlockType?.label}`,
          })
        );
      }

      // update action
      setAction(infoBlock ? VENUE_ACTIONS.editInfoBlock : VENUE_ACTIONS.createInfoBlock);

      // clear delete action in case edit was empty and therefore delete was dispatched
      dispatch(clearActionMeta(VENUE_ACTIONS.deleteInfoBlock));
    }
  }, [apiMeta?.status, action, dispatch, infoBlock, infoBlockType, apiOrigin]);

  const handleSubmit = (data: Partial<IInfoBlock>) => {
    const mergedData = {
      ...infoBlock,
      ...data,
      [submitData.type]: submitData.id,
      type: infoBlockType.type,
    } as IInfoBlock;

    if (infoBlock) {
      const path =
        submitData.type === "venue"
          ? API_ENDPOINTS.VENUE_INFO_BLOCK(infoBlock.id)
          : API_ENDPOINTS.STAGE_INFO_BLOCK(infoBlock.id);
      dispatch(editInfoBlock({ ...mergedData, path } as IInfoBlockWithPath));
    } else {
      dispatch(createInfoBlock(mergedData));
    }
  };

  const clearInfoBlockMeta = useCallback(() => {
    if (action) {
      dispatch(clearActionMeta(action));
    }
  }, [dispatch, action]);

  if (action === "") return <Loader />;

  return (
    <Card
      icon={icon}
      title={`${
        action === VENUE_ACTIONS.editInfoBlock ? copy.actions.editing : copy.actions.adding
      } ${infoBlockType?.parentLabel}: ${infoBlockType?.label}`}
    >
      <Spacer size="l">
        <InfoBlockForm
          handleSubmit={handleSubmit}
          blockExists={action === VENUE_ACTIONS.editInfoBlock}
          apiStatus={apiOrigin === IInfoBlockAPIOrigin.Form ? apiMeta?.status : undefined}
          hasError={!!apiMeta?.error && apiOrigin === IInfoBlockAPIOrigin.Form}
          defaultValues={infoBlock ?? undefined}
        />
        <EditInfoBlockItems
          infoBlockLabel={infoBlockType.label}
          defaultValues={infoBlock}
          handleSubmit={handleSubmit}
          infoBlockApiStatus={apiMeta?.status}
          infoBlockHasError={!!apiMeta?.error}
          apiOrigin={apiOrigin}
          setApiOrigin={setApiOrigin}
          clearInfoBlockMeta={clearInfoBlockMeta}
          userPermissions={userPermissions}
        />
      </Spacer>
    </Card>
  );
};
